import React, { useState } from 'react';
import { isNil, map, assoc, filter, propEq, omit } from 'ramda';
import styled, { css } from 'styled-components';
import CollapsibleBlock from '~/components/CollapsibleBlock';
import { AccordionGroup } from '../..';
import ToggleCheckbox from '~/components/ToggleCheckbox';
import JustificationContainer from '~/components/JustificationContainer';
import TextButton from '~/components/TextButton';
import { useRecoilState } from 'recoil';
import Pin from '../Pin';
import pinnedAppsState, {
  PinnedApp,
  PinnedAppsMap,
} from '~/scenes/Settings/Widget/states/pinnedApps';
import GroupItems from '../GroupItems';
import { pinOptions } from '../../__fixtures__';
import alterById from '~/util/alterById';
import getPinnedItemsForGroup from '../../utils/getPinnedItemsForGroup';
import itemIsPinned from '../../utils/itemIsPinned';
import generatePinnedItem from '../../utils/generatePinnedItem';
import Loading from '~/components/Loading';

export type Props = {
  dataTestId?: string;

  /** Group id */
  id: string;

  /** Anchor id */
  anchorId: string;

  /** Group name */
  name: string;

  /** Items to show in the nested block */
  items: Array<AccordionGroup> | null;

  /** checked state of the group */
  checked: boolean;

  /** Removes checkboxes to only display text */
  selectable: boolean;

  /** Changes groupLookup state */
  onChange: (args: {
    items?: Array<AccordionGroup> | null;
    mainChecked?: boolean;
  }) => void;
};

export type PinAction = 'pin' | 'unpin';

const text = {
  selectAll: 'Selecteer alles',
  deselectAll: 'Deselecteer alles',
};

const Group: React.FC<Props> = ({
  dataTestId,
  id,
  name,
  selectable,
  items,
  checked: mainChecked,
  onChange: onParentChange,
  ...rest
}) => {
  const [contentOpen, setContentOpen] = useState<boolean>(!isNil(items));
  const [pinnedApps, setPinnedApps] = useRecoilState(pinnedAppsState);

  // Should not happen because these components will be loaded only if pinnedAppsState is not null (handled in Widget/index.tsx:109),
  // but for the type's sake
  if (pinnedApps === null) return <Loading />;

  const hasItems = !isNil(items);
  const checkedAmount = hasItems
    ? filter(propEq('checked', true), items).length
    : 0;
  const isGroupChecked = hasItems ? checkedAmount === items.length : false;
  const partiallyChecked = hasItems
    ? !isGroupChecked && checkedAmount > 0
    : false;

  const allChecked = isGroupChecked || partiallyChecked;

  const onUnpin = (itemsToUnpin: Array<string>) => {
    const updatedItems = omit(itemsToUnpin, pinnedApps);
    setPinnedApps(updatedItems);
  };

  const onPin = (updatedItems: PinnedAppsMap, item: PinnedApp) =>
    setPinnedApps({
      ...updatedItems,
      [item.id]: item.pinned,
    });

  const onMainCheckedChange = () => {
    const update = !mainChecked;

    if (items && items.length > 0) {
      setContentOpen(true);

      if (update === false) {
        // If pinned items in a group and app is being turned off, unpin those items
        const pinnedAppsPerGroup = getPinnedItemsForGroup({
          items,
          pinnedApps,
        });
        onUnpin(pinnedAppsPerGroup);
      }
    }

    // When there are no items, unpin app if it is being turned off
    if (itemIsPinned(id, pinnedApps) && update === false) {
      onUnpin([id]);
    }

    onParentChange({ mainChecked: update });
  };

  const markAllCheckboxes = () => {
    if (!items) return;

    const selectAll = partiallyChecked ? true : !allChecked;

    if (selectAll === false) {
      const pinnedAppsPerGroup = getPinnedItemsForGroup({ items, pinnedApps });
      onUnpin(pinnedAppsPerGroup);
    }

    onParentChange({ items: map(assoc('checked', selectAll), items) });
  };

  const selectAll = !isGroupChecked || partiallyChecked;

  const toggleBlock = () => {
    if (hasItems) setContentOpen(prevOpen => !prevOpen);
  };

  const onItemChange = (id: string, value: boolean) => {
    if (!items) return;

    if (itemIsPinned(id, pinnedApps) && value === false) {
      // Unpin item when app is being turned off
      onUnpin([id]);
    }

    const updated = alterById(id, 'checked', value, items);
    onParentChange({ items: updated });
  };

  const onPinChange = (type: PinAction, item: PinnedApp) => {
    if (type === 'unpin') {
      return onUnpin([item.id]);
    }

    const itemWithTheSameWeight = Object.keys(pinnedApps).find(
      key => pinnedApps[key].weight === item.pinned.weight,
    );
    const sameItemIsClicked = !isNil(pinnedApps[item.id]);

    if (sameItemIsClicked) {
      // Item with the same weight will be removed
      if (itemWithTheSameWeight) {
        const updatedItems = omit([itemWithTheSameWeight], pinnedApps);
        return onPin(updatedItems, item);
      }

      // Changes the weight of the same item
      if (pinnedApps[item.id].weight !== item.pinned.weight) {
        return onPin(pinnedApps, item);
      }
    }

    // Item will replace the previous item with this weight
    if (itemWithTheSameWeight) {
      const updatedItems = omit([itemWithTheSameWeight], pinnedApps);
      return onPin(updatedItems, item);
    }

    // Pin item
    return onPin(pinnedApps, item);
  };

  const pinnedItem = pinnedApps[id]
    ? { id, pinned: pinnedApps[id] }
    : undefined;

  const selectedOption = pinOptions.find(
    option => option.value === pinnedItem?.pinned.weight,
  );

  return (
    <CollapsibleBlock
      data-testid={dataTestId}
      id={id}
      header={
        <HeaderContainer align="center" justification="space-between">
          <Label
            gap={!hasItems ? 'base' : undefined}
            margin={hasItems ? [null, 'base'] : [null]}
            onClick={toggleBlock}
            $clickable={hasItems}
          >
            {!hasItems && (
              <Pin
                disabled={!mainChecked}
                selectedOption={selectedOption}
                options={pinOptions}
                onClick={(type, weight) => {
                  const itemToPin = generatePinnedItem({ id, weight });
                  onPinChange(type, itemToPin);
                }}
              />
            )}
            {name}
          </Label>

          <JustificationContainer align="center">
            {contentOpen && mainChecked && (
              <TextButton
                withoutPadding
                onClick={markAllCheckboxes}
                margin={[null, 'l']}
                icon={selectAll ? 'check-square' : 'square'}
                label={selectAll ? text.selectAll : text.deselectAll}
              />
            )}

            <ToggleCheckbox
              value={mainChecked}
              onClick={onMainCheckedChange}
              size="small"
            />
          </JustificationContainer>
        </HeaderContainer>
      }
      itemsLength={items?.length ?? 0}
      variant="primary"
      isOpen={contentOpen}
      onToggle={toggleBlock}
      disabled={!mainChecked}
      {...rest}
    >
      {items && (
        <GroupItems
          pinnedApps={pinnedApps}
          onItemChange={onItemChange}
          selectable={selectable}
          checked={mainChecked}
          onPin={onPinChange}
          items={items}
        />
      )}
    </CollapsibleBlock>
  );
};

const Label = styled(JustificationContainer)<{ $clickable: boolean }>(
  ({ theme, $clickable }) => css`
    font-weight: ${theme.fw('semiBold')};
    cursor: ${$clickable ? 'pointer' : 'auto'};
  `,
);

const HeaderContainer = styled(JustificationContainer)<{}>(
  ({ theme }) => css`
    font-weight: ${theme.fw('semiBold')};
    width: 100%;
  `,
);

export default Group;
